import { z } from 'zod'
import i18n from '@/i18n'
import { MAX_CONTACTS } from '@/lib/clientContacts'
import { isValidPhoneNumber } from '@/lib/phone'

const contactSchema = z.object({
  id: z.string().optional(),
  full_name: z.string().max(150).optional().or(z.literal('')),
  role: z.string().max(100).optional().or(z.literal('')),
  phone: z
    .string()
    .max(24)
    .superRefine((value, ctx) => {
      if (!value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('clients:form.phoneRequired'),
        })
      } else if (!isValidPhoneNumber(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('clients:form.phoneInvalid'),
        })
      }
    }),
  email: z.union([
    z.literal(''),
    z.string().email('Please enter a valid email address.').max(100),
  ]),
})

// 2.4.3 Create client validation. Field requirements vary by client type:
// Business/Property management → company name required; Individual → first + last name.
export const clientFormSchema = z
  .object({
    // Individual / Business (company) / Property management company (#20). The
    // latter two surface the INSEE/SIRENE lookup and require a company name.
    type: z.enum(['individual', 'company', 'property_management']),
    name: z.string().max(150).optional().or(z.literal('')),
    // UI-only for individuals (Nom / Prénom); joined into `name` on submit.
    first_name: z.string().max(100).optional().or(z.literal('')),
    last_name: z.string().max(100).optional().or(z.literal('')),
    contacts: z.array(contactSchema).min(1).max(MAX_CONTACTS),
    siret: z.string().max(20).optional().or(z.literal('')),
    address: z.string().min(1, 'Address is required').max(255),
    address_additional: z.string().max(150).optional().or(z.literal('')),
    postal_code: z.string().min(1, 'Postal code is required').max(16),
    city: z.string().min(1, 'City is required').max(100),
    country: z.string().min(1, 'Country is required'),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    property_type: z.string().optional().or(z.literal('')),
    access_code: z.string().max(50).optional().or(z.literal('')),
    floor: z.string().max(20).optional().or(z.literal('')),
    access_information: z.string().max(1000).optional().or(z.literal('')),
    source: z.string().optional().or(z.literal('')),
    status: z.string().optional().or(z.literal('')),
    notes: z.string().max(2000).optional().or(z.literal('')),
  })
  .superRefine((val, ctx) => {
    // Business + Property management require a company name; Individual requires
    // Nom + Prénom (#20 / Figma side panel).
    if (val.type !== 'individual') {
      if (!val.name || val.name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['name'],
          message: 'Company name is required.',
        })
      }
    } else {
      if (!val.last_name || val.last_name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['last_name'],
          message: 'Last name is required.',
        })
      }
      if (!val.first_name || val.first_name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['first_name'],
          message: 'First name is required.',
        })
      }
    }

    const max = MAX_CONTACTS
    if (val.contacts.length > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contacts'],
        message: `At most ${max} contacts are allowed.`,
      })
    }

    val.contacts.forEach((contact, i) => {
      if (!contact.full_name || contact.full_name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['contacts', i, 'full_name'],
          message: 'Contact name is required.',
        })
      }
    })
  })

export type ClientFormValues = z.infer<typeof clientFormSchema>

export const emptyContact = (): ClientFormValues['contacts'][number] => ({
  full_name: '',
  role: '',
  phone: '',
  email: '',
})

/** Join Prénom + Nom into a single name for the API. */
export function joinIndividualContactName(
  firstName: string | undefined,
  lastName: string | undefined
): string {
  return [firstName?.trim(), lastName?.trim()].filter(Boolean).join(' ')
}

/**
 * Best-effort split of stored name into first/last for the individual
 * form. Last token → last_name; remainder → first_name.
 */
export function splitContactName(contactName: string | null | undefined): {
  first_name: string
  last_name: string
} {
  const trimmed = (contactName ?? '').trim()
  if (!trimmed) return { first_name: '', last_name: '' }
  const parts = trimmed.split(/\s+/)
  if (parts.length === 1) {
    return { first_name: '', last_name: parts[0] }
  }
  return {
    first_name: parts.slice(0, -1).join(' '),
    last_name: parts[parts.length - 1],
  }
}
